import { useQuery } from '@tanstack/react-query'
import { MINUTE_MS } from 'data/constants'
import { gql, request } from 'graphql-request'
import { type Address, useBaseReads } from 'use-towns-client'

const BOT_TIPS_QUERY = gql`
    query GetBotTips($appAddress: String!) {
        app(address: $appAddress) {
            tipsVolume
            tipsCount
        }
    }
`

interface BotTipsResponse {
    app: {
        tipsVolume: string | null
        tipsCount: number | null
    } | null
}

export interface BotTipsData {
    totalTips: bigint
    tipCount: bigint
}

const fetchBotTips = async (
    appAddress: Address | undefined,
    subgraphEndpoint: string | undefined,
): Promise<BotTipsData> => {
    if (!appAddress || !subgraphEndpoint) {
        return {
            totalTips: 0n,
            tipCount: 0n,
        }
    }

    try {
        const data = await request<BotTipsResponse>(subgraphEndpoint, BOT_TIPS_QUERY, {
            appAddress: appAddress.toLowerCase(),
        })

        const tipsVolume = data?.app?.tipsVolume ?? '0'
        const tipsCount = data?.app?.tipsCount ?? 0

        return {
            totalTips: BigInt(tipsVolume),
            tipCount: BigInt(tipsCount),
        }
    } catch (error) {
        console.error('[useBotTips] Error fetching bot tips:', error)
        return {
            totalTips: 0n,
            tipCount: 0n,
        }
    }
}

export const useBotTips = (appAddress: Address | undefined) => {
    const { subgraphEndpoint } = useBaseReads()

    return (
        useQuery({
            queryKey: ['botTips', appAddress],
            queryFn: () => fetchBotTips(appAddress, subgraphEndpoint),
            enabled: !!appAddress && !!subgraphEndpoint,
            refetchInterval: MINUTE_MS * 5,
            refetchOnWindowFocus: false,
            refetchIntervalInBackground: false,
        }).data ?? {
            totalTips: 0n,
            tipCount: 0n,
        }
    )
}
